/* eslint-disable react/prop-types */
import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  message,
  Row,
  Spin,
  Tabs,
  Result as AntResult,
} from 'antd';
import {
  AlertOutlined,
  EnvironmentOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import { onError } from 'utils/handlers';
import makeMessager from 'utils/complexFormsMessages';
import { makeGetOnFinish, makeOnChangeTabKey } from 'utils/tabsManagement';
import {
  ActionsNames,
  getModelPermission,
  hasActionPermission,
  isInspector,
} from 'utils/permission/index';
import address from 'utils/address';
import { useHistory } from 'react-router-dom';
import {
  deleteMulta,
  getMulta,
  getMultas,
  getMultasCaja,
  patchMulta,
  postMulta,
} from 'api/recaudacion/multas';
import { getUsuario, getUsuarios } from 'api/usuarios/usuarios';
import {
  DATE_FRONTEND_FORMAT,
  TIMEPICKER_FORMAT,
} from 'utils/formatValues';
import { getDepartamentos } from 'api/configuracion/departamentos';
import { InputSearchPro } from 'components/InputSearch';
import BusquedaAvanzadaUsers from 'components/BusquedaAvanzadaCajeros';
import Direccion from 'components/Direccion';
import { getSectoresDeMultas } from 'api/recaudacion/sectorDeMulta';
import { getTiposDeObras } from 'api/recaudacion/tipoDeObras';
import { getClasesDeMultas } from 'api/recaudacion/claseDeMulta';
import { getEstadosDeMultas } from 'api/catalogos/estadoDeMultas';
import { getDependencias } from 'api/configuracion/dependencias';
import { getCatalogoDePadrones, getContentTypesPadrones } from 'api/catalogos';
import { getHelpTexts, getTipoDePadronPorContenType } from 'utils/padrones';
import { getContribuyenteCaja } from 'api/empresas/contribuyentes';
import BusquedaAvanzadaPadrones from 'views/Recaudacion/Caja/BusquedaAvanzadaPadrones';
import { padronesEnum } from 'views/Tramites/GPM/enums';
import { getEmpresaCaja } from 'api/empresas';
import { getPredioCaja } from 'api/catastro';
import { getHospedajeCaja } from 'api/empresas/hospedajes';
import { getArrendamientoCaja } from 'api/empresas/arrendamientos';
import { getNominaCaja } from 'api/empresas/nomina';
import { getAlcoholCaja } from 'api/empresas/alcoholes';
import { getJuegosDeAzarCaja } from 'api/empresas/juegosDeAzar';
import { getNotariosCaja } from 'api/empresas/notarios';
import { getCedulaCaja } from 'api/empresas/cedulas';
import { getCasasDeEmpenioCaja } from 'api/empresas/casasDeEmpenio';
import { getAgenciasCaja } from 'api/empresas/agencias';
import { getMotocicletaCaja } from 'api/recaudacion/motocicletas';
import { getRemolqueCaja } from 'api/recaudacion/remolque';
import { getZonasExclusivasCaja } from 'api/empresas/zonasExclusivas';
import { getLicenciasDeFuncionamiento } from 'api/empresas/licenciasDeFuncionamiento';
import { getExpedienteDeAnunciosCaja } from 'api/empresas/expedienteDeAnuncios';
import { getContratosDePoliciasCaja } from 'api/empresas/contratosDePolicias';
import { getExpedientesDeComercioCaja } from 'api/empresas/expedienteDeComercioInformal';
import { getExpedientesDeMercadoCaja } from 'api/empresas/expedienteDeMercado';
import padronToString from 'utils/padronesToString';
import { toUpper } from 'utils/normalizers';
import styled from 'styled-components';
import MercanciaDecomisos, { modelCrud as modelMercancia } from './MercanciaDecomisos';
import ConceptosDeMultas, { modelCrud as modelConceptos } from './ConceptosDeMultas';

const { TabPane } = Tabs;
const baseURI = '/recaudacion/multas/';

const models = ['tipodeobra', 'sectordemulta', 'clasedemulta'];

const validModels = [
  padronesEnum.EMPRESA,
  padronesEnum.PREDIO,
  padronesEnum.HOSPEDAJE,
  padronesEnum.ARRENDAMIENTO,
  padronesEnum.NOMINA,
  padronesEnum.ALCOHOL,
  padronesEnum.CEDULAR,
  padronesEnum.JUEGO_DE_AZAR,
  padronesEnum.NOTARIO,
  padronesEnum.CASA_DE_EMPENIO,
  padronesEnum.AGENCIA,
  padronesEnum.CONTRIBUYENTE,
  padronesEnum.MOTOCICLETA,
  padronesEnum.REMOLQUE,
  padronesEnum.ZONA_EXCLUSIVA,
  padronesEnum.EXPEDIENTE_LICENCIA_FUNCIONAMIENTO,
  padronesEnum.EXPEDIENTE_DE_ANUNCIO,
  18, // contraro de policia
  20, // Expediente de comercio informal
  21, // Expediente de mercado
  25, // multa
];

const getters = {
  [padronesEnum.EMPRESA]: getEmpresaCaja,
  [padronesEnum.PREDIO]: getPredioCaja,
  [padronesEnum.HOSPEDAJE]: getHospedajeCaja,
  [padronesEnum.ARRENDAMIENTO]: getArrendamientoCaja,
  [padronesEnum.NOMINA]: getNominaCaja,
  [padronesEnum.ALCOHOL]: getAlcoholCaja,
  [padronesEnum.CEDULAR]: getCedulaCaja,
  [padronesEnum.JUEGO_DE_AZAR]: getJuegosDeAzarCaja,
  [padronesEnum.NOTARIO]: getNotariosCaja,
  [padronesEnum.CASA_DE_EMPENIO]: getCasasDeEmpenioCaja,
  [padronesEnum.AGENCIA]: getAgenciasCaja,
  [padronesEnum.CONTRIBUYENTE]: getContribuyenteCaja,
  [padronesEnum.MOTOCICLETA]: getMotocicletaCaja,
  [padronesEnum.REMOLQUE]: getRemolqueCaja,
  [padronesEnum.ZONA_EXCLUSIVA]: getZonasExclusivasCaja,
  [padronesEnum.EXPEDIENTE_LICENCIA_FUNCIONAMIENTO]: getLicenciasDeFuncionamiento,
  [padronesEnum.EXPEDIENTE_DE_ANUNCIO]: getExpedienteDeAnunciosCaja,
  18: getContratosDePoliciasCaja,
  20: getExpedientesDeComercioCaja,
  21: getExpedientesDeMercadoCaja,
  25: getMultasCaja,
};

const Multas = ({ permission }) => {
  const history = useHistory();
  const addressPermission = useMemo(() => getModelPermission('direccion'), []);
  const estadosGlobales = useSelector(({ catalogs }) => catalogs.estadosGlobales);
  const entidad = useSelector(({ auth }) => auth.entidad);
  const [form] = Form.useForm();
  const [formDetalles] = Form.useForm();
  const [formInfractor] = Form.useForm();
  const [formMercancia] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visibleAdvancedUser, setVisibleAdvancedUser] = useState(false);
  const [currentTabKey, setCurrentTabKey] = useState('general');
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState({ results: [] });
  const [currentPage, setCurrentPage] = useState(1);
  const [hasAllViewPermission, setHasAllViewPermission] = useState(true);
  const [departamentos, setDepartamentos] = useState([]);
  const [sector, setSector] = useState([]);
  const [tipoDeObra, setTipoDeObra] = useState([]);
  const [userFound, setUserFound] = useState([]);
  const [formAddress, setFormAddress] = useState();
  const [formAddressMulta, setFormAddressMulta] = useState();
  const [permissions, setPermissions] = useState(null);
  const [claseDeMulta, setClaseDeMulta] = useState([]);
  const [estadoDeMulta, setEstadoDeMulta] = useState([]);
  const [dependencias, setDependencias] = useState([]);
  const [filteredDepartamentos, setFilteredDepartamentos] = useState([]);
  const [contentTypes, setContentTypes] = useState([]);
  const [tipoDePadronSelected, setTipoDePadronSelected] = useState(0);
  const [visibleBusquedaPadrones, setVisibleBusquedaPadrones] = useState(false);
  const [dataExterno, setDataExterno] = useState();
  const [padron, setPadron] = useState(null);
  const [fieldName, setFieldName] = useState('nombre_ciudadano');
  const [selectedPadron, setSelectedPadron] = useState(1);

  const basicKeys = [
    'conceptos',
    'detalles',
    'general',
    'info',
    'infractor',
    'mercancia',
    'motivos',
  ];

  const complexForms = [
    {
      key: 'direccion_multa',
      formInstance: formAddress,
      permission: addressPermission,
    },
    {
      key: 'direccion_notificacion_multa',
      formInstance: formAddressMulta,
      permission: addressPermission,
    },
  ];

  const formInstances = [form, formDetalles, formInfractor, formMercancia];

  const fetchData = async (page = 1, q = null) => {
    setLoading(true);
    setCurrentPage(page);
    const _data = await getMultas({
      ...q,
      page,
    });
    setData(_data);
    setLoading(false);
  };

  const showMsg = makeMessager(setCurrentTabKey, fetchData);

  useEffect(() => {
    let mounted = true;
    let _hasAllPermission = true;
    const validatePermission = () => {
      const esInspector = isInspector();
      const mercancia = getModelPermission(modelMercancia);
      const conceptos = getModelPermission(modelConceptos);
      const _hasAllViewPermission = models
        .map((m) => hasActionPermission(m, ActionsNames.view))
        .every((e) => e);
      if (!_hasAllViewPermission) {
        message.warn('Hacen falta permisos en los catálogos relacionados a este módulo.');
      }

      if (mounted) {
        _hasAllPermission = _hasAllViewPermission && esInspector;
        setHasAllViewPermission(_hasAllViewPermission);
        setPermissions({
          mercancia,
          conceptos,
        });
      }
    };
    const fetchAll = async () => {
      setLoading(true);
      const [
        _data,
        _departamentos,
        _sector,
        _obras,
        _clases,
        _estadoMultas,
        _dependencias,
        _contentTypesPadrones,
      ] = await Promise.all([
        getMultas({ page: 1 }, true),
        getDepartamentos({ es_para_multas: true }),
        getSectoresDeMultas(),
        getTiposDeObras(),
        getClasesDeMultas(),
        getEstadosDeMultas(),
        getDependencias({ es_para_multas: true }),
        getContentTypesPadrones(),
        getCatalogoDePadrones(),
      ]);
      if (mounted) {
        setData(_data);
        setDepartamentos(_departamentos);
        setSector(_sector);
        setTipoDeObra(_obras);
        setClaseDeMulta(_clases);
        setEstadoDeMulta(_estadoMultas);
        setDependencias(_dependencias);
        setContentTypes(_contentTypesPadrones.filter((ct) => validModels.includes(ct.nivel)));
        setLoading(false);
      }
    };
    validatePermission();
    if (_hasAllPermission) {
      fetchAll();
    }
    return () => { mounted = false; };
  }, []);

  const tipoDePadronIdFromContentype = (contentTypeId) => ((contentTypeId && contentTypes.length)
    ? contentTypes.find((ct) => ct.id === contentTypeId)?.nivel : 0);

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
    formDetalles.resetFields();
    formInfractor.resetFields();
    formMercancia.resetFields();
    setTipoDePadronSelected(0);
    setSelectedPadron(1);
    setPadron();
    setSelectedRowKeys([]);
    setUserFound([]);
    setCurrentTabKey('general');
    setFilteredDepartamentos([]);
  };

  const reset = () => {
    setUserFound([]);
    form.resetFields(['q']);
  };

  const searchUsuario = async () => {
    try {
      setLoading(true);
      await form.validateFields(['q']);
      const { q: email } = form.getFieldsValue();
      if (!email) {
        setLoading(false);
        message.warn('El campo de búsqueda está vacío');
        return;
      }
      const resUser = await getUsuarios({ email });
      if (resUser.length) {
        form.setFieldsValue({
          usuario_inspector: resUser[0].id,
        });
        const _usuario = resUser.map((e) => ({
          ...e,
          nombre_completo: `${e.first_name} ${e.last_name || ''} ${e.second_last_name || ''}`,
        }));
        setUserFound([_usuario[0]]);
      } else {
        message.info('No se encontraron coincidencias');
        setUserFound([]);
      }
      setLoading(false);
    } catch (error) {
      onError(error, setLoading);
    }
  };

  const fetchUser = async (id) => {
    setLoading(true);
    if (id) {
      const _user = await getUsuario(id?.id || id);
      if (_user) {
        form.setFieldsValue({
          usuario_inspector: _user.id,
        });
        const matched = {
          ..._user,
          nombre_completo: `${_user?.first_name} ${_user?.last_name
            || ''} ${_user?.second_last_name || ''}`,
        };
        setUserFound(matched);
      } else {
        message.info('No se encontró usuario');
      }
    }
    setLoading(false);
  };

  const onClickEdit = async (id) => {
    const key = id || selectedRowKeys[0];
    const match = await getMulta(key);
    if (match) {
      if (match.padron_content_type && match.padron_object_id) {
        const padronid = getTipoDePadronPorContenType(match.padron_content_type);
        const getPadron = getters[padronid];
        const response = await getPadron({ id: match.padron_object_id });
        const _padron = Array.isArray(response) ? response[0] : response.results[0];
        _padron.nombre_completo = padronToString(_padron, padronid);
        setPadron(_padron);
      }
      await fetchUser(match.usuario_inspector);
      if (match.unidad_operativa) {
        const filtered = departamentos
          .filter((d) => d.unidad_operativa.id === match.unidad_operativa);
        setFilteredDepartamentos(filtered);
      }
      setTimeout(() => {
        form.setFieldsValue(match);
        formDetalles.setFieldsValue(match);
        formInfractor.setFieldsValue(match);
        formMercancia.setFieldsValue(match);
      });
    }
    setVisible(true);
  };

  const onFinish = async (_continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      await formDetalles.validateFields();
      await formInfractor.validateFields();
      await formMercancia.validateFields();
      const values = {
        ...form.getFieldsValue(),
        ...formDetalles.getFieldsValue(),
        ...formInfractor.getFieldsValue(),
        ...formMercancia.getFieldsValue(),
      };
      const [key] = selectedRowKeys;
      const _data = await (key
        ? patchMulta(key, values) : postMulta(values));
      if (_data) {
        await fetchData();
        if (_continue) {
          if (!key) {
            setSelectedRowKeys([_data.id]);
          }
          await onClickEdit(_data.id);
        } else {
          onCancel();
        }
        setLoading(false);
        return _data;
      }
      setLoading(false);
      return true;
    } catch (err) {
      onError(err, setLoading, formInstances, setCurrentTabKey);
      return false;
    }
  };

  const deleteItem = async () => {
    setLoading(true);
    if (selectedRowKeys.length) {
      const [key] = selectedRowKeys;
      const deleted = await deleteMulta(key);
      if (deleted) {
        setVisibleAlert(false);
        onCancel();
        await fetchData();
      }
    }
    setLoading(false);
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const columns = [
    {
      titleText: 'Clave',
      dataIndex: 'clave',
      key: 'clave',
      width: 200,
    },
    {
      titleText: 'Folio',
      dataIndex: 'folio',
      key: 'folio',
      width: 200,
    },
    {
      titleText: 'Folio Interno',
      dataIndex: 'folio_interno',
      key: 'folio_interno',
      width: 200,
    },
    {
      titleText: 'Dirección de Notificación',
      dataIndex: 'direccion_notificacion_multa',
      key: 'direccion_notificacion_multa',
      render: (val) => (val ? address(val) : 'Multa sin dirección de Notificación'),
      width: 300,
    },
    {
      titleText: 'Dirección de Multa ',
      dataIndex: 'direccion_multa',
      key: 'direccion_multa',
      render: (val) => (val ? address(val) : 'Multa sin dirección de Notificación'),
      width: 300,
    },
    {
      titleText: 'Estatus de Expediente',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 200,
      catalog: {
        cat: estadosGlobales,
        labelProp: 'descripcion',
        use: true,
      },
    },
  ];

  const rules = {
    required: [
      { required: true },
    ],
  };

  const onChangeTabKey = makeOnChangeTabKey(
    basicKeys, complexForms, currentTabKey, setCurrentTabKey, showMsg,
  );

  const getOnFinish = makeGetOnFinish(basicKeys, complexForms, currentTabKey, onFinish);

  const filterDepartamentos = (dependenciaId) => {
    form.resetFields(['unidad_responsable']);
    if (!dependenciaId) {
      setFilteredDepartamentos([]);
      return;
    }
    const filtered = departamentos.filter((d) => d.unidad_operativa.id === dependenciaId);
    setFilteredDepartamentos(filtered);
  };

  const handleSearchPadron = async () => {
    setLoading(true);
    await formInfractor.validateFields(['q']);
    const { q, padron_content_type } = formInfractor.getFieldsValue();
    if (!q || !padron_content_type) {
      setLoading(false);
      message.warn('El campo de búsqueda esta vacío');
      return;
    }
    const idPadron = tipoDePadronIdFromContentype(padron_content_type);
    const _fieldName = idPadron === 1 ? 'nombre_ciudadano' : 'nombre_contribuyente';
    const dataPadron = await getters[idPadron]({ q });
    if ([0, 1].includes(dataPadron?.results?.length) || [1, 0].includes(dataPadron?.length)) {
      const found = dataPadron?.results?.[0] || dataPadron?.[0];
      if (found) {
        found.nombre_completo = padronToString(found, idPadron);
        setPadron(found);
        formInfractor.setFieldsValue({
          padron_object_id: found.id,
          [_fieldName]: found.nombre_completo,
        });
      } else {
        message.warn('Padrón no encontrado', 3.5);
      }
    } else {
      setDataExterno(dataPadron);
      setVisibleBusquedaPadrones(true);
    }
    setLoading(false);
  };

  const getTitle = () => {
    if (!entidad?.rol_inspector) {
      return 'Se requiere la configuración rol inspector';
    }
    if (!isInspector()) {
      return 'Este usuario no tiene el rol inspector';
    }
    return '';
  };

  return (
    <Row align="center" justify="center" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!visible
          ? (
            <>
              {isInspector() && (
              <Table
                cols={columns}
                mobileColumns={[columns[0], columns[1]]}
                data={data.results || []}
                rowSelection={rowSelection}
                handleOnRowClick={handleOnRowClick}
                controls={{
                  onClickAdd,
                  onClickEdit,
                  onClickDelete,
                }}
                rowKey="id"
                baseURI={baseURI}
                permission={permission}
                pagination={false}
                paginationCallback={fetchData}
                count={data.count}
                currentPage={currentPage}
                hasAllViewPermission={hasAllViewPermission}
              />
              )}
              {!loading && !isInspector() && (
                <Result
                  title={getTitle()}
                  extra={(
                    <Button onClick={() => history.push('/configuracion/entidad?tab=configurations')}>
                      Ir a Configuración de la entidad
                    </Button>
                  )}
                />
              )}
            </>
          ) : (
            <Col span={24} style={{ height: '100%' }}>
              <Card
                className="form-container"
                title={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Multa`}
                extra={(
                  <FormSubmitControls
                    onFinish={getOnFinish()}
                    onCancel={onCancel}
                    allowSaveAndContinue
                    onCancelText="Cancelar"
                    permission={permission}
                  />
                )}
                bordered={false}
              >
                <Tabs
                  onChange={onChangeTabKey}
                  activeKey={currentTabKey}
                  type="card"
                >
                  <TabPane
                    tab={(
                      <span>
                        <InfoCircleOutlined />
                        Información General
                      </span>
                    )}
                    key="general"
                    forceRender
                  >
                    <Form
                      name="general"
                      layout="vertical"
                      form={form}
                      onFinish={() => onFinish()}
                      scrollToFirstError
                      initialValues={{ estados_globales: 1 }}
                    >
                      <Row gutter={10}>
                        {!!selectedRowKeys.length && (
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="clave"
                            label="clave"
                            rules={rules.required}
                            hasFeedback
                          >
                            <Input allowClear disabled={!!selectedRowKeys.length} />
                          </Form.Item>
                        </Col>
                        )}
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="folio"
                            label="Folio"
                            hasFeedback
                            normalize={toUpper}
                          >
                            <Input allowClear />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="folio_interno"
                            label="Folio Interno"
                            rules={rules.required}
                            hasFeedback
                            normalize={toUpper}
                          >
                            <Input allowClear />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={10}>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="clase_de_multa"
                            label="Clase de Multa"
                            hasFeedback
                            rules={rules.required}
                          >
                            <Select dataSource={claseDeMulta} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="estado_de_multa"
                            label="Estado de la Multa"
                            hasFeedback
                          >
                            <Select dataSource={estadoDeMulta} />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={10}>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="hora_multa"
                            rules={rules.required}
                            label="Hora de Multa"
                            hasFeedback
                          >
                            <DatePicker.TimePicker
                              placeholder=""
                              format={TIMEPICKER_FORMAT}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="fecha_de_multa"
                            rules={rules.required}
                            label="Fecha Multa"
                          >
                            <DatePicker
                              placeholder=""
                              format={DATE_FRONTEND_FORMAT}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={10}>
                        <InputSearchPro
                          label="Inspector"
                          tooltip="Búsqueda por Correo del Inpsector"
                          name="q"
                          rules={rules.required}
                          onClickAdvanced={() => setVisibleAdvancedUser(true)}
                          onPressEnter={() => searchUsuario()}
                          breakPoints={{
                            md: 16,
                            lg: 8,
                          }}
                          resultProps={{
                            name: 'usuario_inspector',
                            label: 'Usuario Inspector',
                            dataSource: userFound,
                            labelProp: 'nombre_completo',
                            onClear: () => reset(),
                            breakPoints: {
                              md: 16,
                              lg: 8,
                            },
                          }}
                        />
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="unidad_operativa"
                            rules={rules.required}
                            label="Dependencia"
                            hasFeedback
                          >
                            <Select
                              dataSource={dependencias}
                              keyLabelRender
                              onChange={filterDepartamentos}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="unidad_responsable"
                            rules={rules.required}
                            label="Departamento"
                            hasFeedback
                          >
                            <Select
                              dataSource={filteredDepartamentos}
                              keyLabelRender
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="zona"
                            label="Zona"
                            hasFeedback
                            normalize={toUpper}
                          >
                            <Input allowClear />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="sector"
                            label="Sector de Multa"
                            hasFeedback
                            rules={rules.required}
                          >
                            <Select dataSource={sector} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="cuadro"
                            label="Cuadro"
                            hasFeedback
                            normalize={toUpper}
                          >
                            <Input allowClear />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="estados_globales"
                            label="Estado"
                            hasFeedback
                            rules={rules.required}
                          >
                            <Select
                              disabled={!selectedRowKeys.length}
                              dataSource={estadosGlobales}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={16}>
                          <Form.Item
                            name="detalle_general"
                            label="Detalle General"
                            hasFeedback
                          >
                            <Input.TextArea autoSize={{ minRows: 3, maxRows: 3 }} />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Form.Item hidden>
                        <Button htmlType="submit" />
                      </Form.Item>
                    </Form>
                  </TabPane>
                  <TabPane
                    tab={(
                      <span>
                        <InfoCircleOutlined />
                        Información del Infractor
                      </span>
                    )}
                    key="infractor"
                    forceRender
                  >
                    <Form
                      name="infractor"
                      form={formInfractor}
                      layout="vertical"
                      onFinish={() => onFinish()}
                      scrollToFirstError
                      initialValues={{
                        padron_content_type: 90,
                      }}
                    >
                      <Row gutter={10}>

                        <InputSearchPro
                          prefix={{
                            name: 'padron_content_type',
                            label: 'Tipo de infractor',
                            rules: rules.required,
                            dataSource: contentTypes,
                            render: (e) => e.model,
                            onChange: (value) => {
                              const padronid = tipoDePadronIdFromContentype(value);
                              const _fieldName = padronid === 1
                                ? 'nombre_ciudadano' : 'nombre_contribuyente';
                              setTipoDePadronSelected(padronid);
                              setFieldName(_fieldName);
                            },
                            width: '100%',
                          }}
                          name="q"
                          label="Búsqueda de padrón"
                          rules={rules.required}
                          tooltip={getHelpTexts(tipoDePadronIdFromContentype(tipoDePadronSelected))}
                          onClickAdvanced={() => setVisibleBusquedaPadrones(true)}
                          breakPoints={{
                            md: 24,
                            xl: 16,
                            xxl: 8,
                          }}
                          onPressEnter={() => handleSearchPadron()}
                          resultProps={{
                            name: 'padron_object_id',
                            label: 'padron',
                            dataSource: padron,
                            labelProp: 'nombre_completo',
                            onClear: () => {
                              setPadron();
                              formInfractor.resetFields(['padron_content_type', 'q']);
                            },
                            breakPoints: {
                              md: 24,
                              xl: 16,
                              xxl: 8,
                            },
                          }}
                        />
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            label="Nombre"
                            name={fieldName}
                            hasFeedback
                          >
                            <Input allowClear />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="identificacion"
                            label="Identificación"
                            hasFeedback
                          >
                            <Input allowClear />
                          </Form.Item>
                        </Col>
                        {selectedPadron === 3 && (
                          <Col xs={24} sm={24} md={8}>
                            <Form.Item
                              name="clave_catastral"
                              label="Clave catastral"
                            >
                              <Input allowClear />
                            </Form.Item>
                          </Col>
                        )}
                      </Row>
                      {selectedPadron === 4 && (
                        <Row gutter={10}>
                          <Col xs={24} sm={24} md={8}>
                            <Form.Item
                              name="serie_vehiculo"
                              label="Serie Vehícular"
                            >
                              <Input allowClear />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={24} md={8}>
                            <Form.Item
                              name="color"
                              label="Color"
                            >
                              <Input allowClear />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={24} md={8}>
                            <Form.Item
                              name="modelo"
                              label="Modelo"
                            >
                              <Input allowClear />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={24} md={8}>
                            <Form.Item
                              name="placa"
                              label="Placa"
                            >
                              <Input allowClear />
                            </Form.Item>
                          </Col>
                        </Row>
                      )}
                    </Form>
                  </TabPane>
                  <TabPane
                    tab={(
                      <>
                        <ExclamationCircleOutlined />
                        Detalles de obra
                      </>
                    )}
                    key="detalles"
                    forceRender
                  >
                    <Form
                      name="detalles"
                      layout="vertical"
                      form={formDetalles}
                      onFinish={() => onFinish()}
                      scrollToFirstError
                      initialValues={{ estados_globales: 1 }}
                    >
                      <Row gutter={10}>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="tipo_de_obra"
                            label="Tipo de Obra"
                            hasFeedback
                            rules={rules.required}
                          >
                            <Select dataSource={tipoDeObra} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="supervisor"
                            label="Supervisor"
                            hasFeedback
                            rules={rules.required}
                            normalize={toUpper}
                          >
                            <Input allowClear />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Form>
                  </TabPane>
                  {addressPermission?.view && (
                    <TabPane
                      tab={(
                        <>
                          <EnvironmentOutlined />
                          Dirección de Multa
                        </>
                      )}
                      key="direccion_multa"
                      forceRender
                      disabled={!selectedRowKeys.length}
                    >
                      <Direccion
                        currentURI={baseURI}
                        parentID={selectedRowKeys[0]}
                        setLoading={setLoading}
                        setFormAddress={setFormAddress}
                        setCurrentTabKey={setCurrentTabKey}
                        addressPropName="direccion_multa"
                        callback={(values, _continue) => {
                          if (!_continue) {
                            onCancel();
                          } else {
                            const [key] = selectedRowKeys;
                            const clone = [...data];
                            const mappedData = clone.map((e) => (e.id === key
                              ? ({ ...e, direccion_multa: values?.id }) : e));
                            setData(mappedData);
                          }
                        }}
                        permission={addressPermission}
                      />
                    </TabPane>
                  )}
                  {addressPermission?.view && (
                  <TabPane
                    tab={(
                      <>
                        <EnvironmentOutlined />
                        Dirección de Notificacion de Multa
                      </>
                      )}
                    key="direccion_notificacion_multa"
                    forceRender
                    disabled={!selectedRowKeys.length}
                  >
                    <Direccion
                      currentURI={baseURI}
                      parentID={selectedRowKeys[0]}
                      setLoading={setLoading}
                      setFormAddress={setFormAddressMulta}
                      setCurrentTabKey={setCurrentTabKey}
                      addressPropName="direccion_notificacion_multa"
                      callback={(values, _continue) => {
                        if (!_continue) {
                          onCancel();
                        } else {
                          const [key] = selectedRowKeys;
                          const clone = [...data];
                          const mappedData = clone.map((e) => (e.id === key
                            ? ({ ...e, direccion_notificacion_multa: values?.id }) : e));
                          setData(mappedData);
                        }
                      }}
                      permission={addressPermission}
                    />
                  </TabPane>
                  )}
                  {permissions?.mercancia?.view && (
                  <TabPane
                    tab={(
                      <>
                        <ExclamationCircleOutlined />
                        Mercancia Decomisos
                      </>
                      )}
                    key="mercancia"
                    forceRender
                    disabled={!selectedRowKeys.length}
                  >
                    <Form
                      name="mercancia"
                      form={formMercancia}
                      layout="vertical"
                    >
                      <Row>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="numero_permiso"
                            label="Número de Permiso"
                            hasFeedback
                          >
                            <Input allowClear />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Form>
                    <MercanciaDecomisos
                      multa={selectedRowKeys[0]}
                      permission={permissions?.mercancia}
                    />
                  </TabPane>
                  )}
                  {permissions?.motivos?.view && (
                  <TabPane
                    tab={(
                      <>
                        <AlertOutlined />
                        Conceptos
                      </>
                      )}
                    key="conceptos"
                    forceRender
                    disabled={!selectedRowKeys.length}
                  >
                    <ConceptosDeMultas
                      multa={selectedRowKeys[0]}
                      permission={permissions?.conceptos}
                    />
                  </TabPane>
                  )}
                </Tabs>
              </Card>
            </Col>
          )}
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          visible={visibleAlert}
          content="Multa seleccionada"
          loading={loading}
        />
        <BusquedaAvanzadaUsers
          visible={visibleAdvancedUser}
          setVisible={setVisibleAdvancedUser}
          callback={fetchUser}
          dataExterno={[]}
          setDataExterno={() => { }}
        />
      </Spin>
      <BusquedaAvanzadaPadrones
        visible={visibleBusquedaPadrones}
        setVisible={setVisibleBusquedaPadrones}
        callback={(found) => {
          const padronId = tipoDePadronIdFromContentype(tipoDePadronSelected);
          const _fieldName = padronId === 1 ? 'nombre_ciudadano' : 'nombre_contribuyente';
          // eslint-disable-next-line no-param-reassign
          found.nombre_completo = padronToString(found, padronId);
          setPadron(found);
          formInfractor.setFieldsValue({
            padron_object_id: found.id,
            [_fieldName]: found.nombre_completo,
          });
        }}
        dataExterno={dataExterno}
        setDataExterno={setDataExterno}
        tipoDePadron={tipoDePadronIdFromContentype(tipoDePadronSelected)}
        allowAdd={false}
      />
    </Row>
  );
};

const Result = styled(AntResult)`
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

export default Multas;
